import type {
  AiExecutionCheckpoint,
  AiExecutionGraph,
  AiExecutionState,
  AiExecutionStepState,
  AiOperationEnvelope,
} from '../../../types/aiOperational'

const graphTransitions: Record<AiExecutionState, AiExecutionState[]> = {
  PLANNED: ['SIMULATED', 'REVIEW_REQUIRED', 'FAILED'],
  SIMULATED: ['REVIEW_REQUIRED', 'APPROVED', 'COMPLETED', 'FAILED'],
  REVIEW_REQUIRED: ['APPROVED', 'FAILED'],
  APPROVED: ['EXECUTING', 'FAILED'],
  EXECUTING: ['COMPLETED', 'FAILED', 'REVERTIBLE'],
  COMPLETED: ['REVERTIBLE'],
  FAILED: [],
  REVERTIBLE: ['REVERTED'],
  REVERTED: [],
}

const stepTransitions: Record<AiExecutionStepState, AiExecutionStepState[]> = {
  PLANNED: ['READY', 'WAITING', 'BLOCKED', 'FAILED'],
  READY: ['WAITING', 'EXECUTING', 'COMPLETED', 'BLOCKED', 'FAILED', 'REVERTIBLE'],
  WAITING: ['READY', 'BLOCKED', 'FAILED'],
  BLOCKED: ['WAITING', 'READY', 'FAILED'],
  EXECUTING: ['COMPLETED', 'FAILED', 'REVERTIBLE'],
  COMPLETED: ['REVERTIBLE'],
  FAILED: [],
  REVERTIBLE: [],
}

export const canTransitionExecutionState = (from: AiExecutionState, to: AiExecutionState) =>
  from === to || graphTransitions[from].includes(to)

export const canTransitionExecutionStepState = (from: AiExecutionStepState, to: AiExecutionStepState) =>
  from === to || stepTransitions[from].includes(to)

export const deriveExecutionState = (envelope: AiOperationEnvelope): AiExecutionState => {
  if (envelope.risk === 'RESTRICTED') return 'FAILED'
  if (envelope.reviewRequired) return 'REVIEW_REQUIRED'
  if (envelope.simulation) return 'SIMULATED'
  return 'PLANNED'
}

export const deriveExecutionStateFromGraph = (graph: AiExecutionGraph): AiExecutionState => {
  if (graph.steps.some((step) => step.state === 'FAILED')) return 'FAILED'
  if (graph.steps.some((step) => step.state === 'EXECUTING')) return 'EXECUTING'
  if (graph.steps.length > 0 && graph.steps.every((step) => step.state === 'REVERTIBLE')) return 'REVERTIBLE'
  if (graph.steps.length > 0 && graph.steps.every((step) => step.state === 'COMPLETED' || step.state === 'REVERTIBLE')) return 'COMPLETED'
  if (graph.checkpoints.some((checkpoint) => checkpoint.state === 'BLOCKED')) return 'FAILED'
  if (graph.checkpoints.some((checkpoint) => checkpoint.state === 'WAITING')) return 'REVIEW_REQUIRED'
  return graph.state
}

export const checkpointStateForStep = (
  checkpoint: AiExecutionCheckpoint,
  stepState: AiExecutionStepState,
): AiExecutionCheckpoint['state'] => {
  if (checkpoint.state) return checkpoint.state
  if (stepState === 'BLOCKED' || checkpoint.satisfiedBy === 'REFUSAL') return 'BLOCKED'
  if (stepState === 'WAITING') return 'WAITING'
  if (stepState === 'READY' || stepState === 'COMPLETED' || stepState === 'REVERTIBLE') return 'SATISFIED'
  return 'PENDING'
}
